import { type ReactNode, useCallback, useMemo } from "react";
import Dialog, { useDialog } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";

type DialogModalOptions = {
  title?: ReactNode;
  header?: ReactNode;
  allowResize?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  dialogContentStyle?: React.CSSProperties;
  className?: string;
};

const maxWidthClassMap: Record<Exclude<DialogModalOptions["maxWidth"], false | undefined>, string> = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};

const DialogModalContent = ({
  title,
  header,
  allowResize = true,
  maxWidth = "sm",
  dialogContentStyle,
  className,
  children,
}: DialogModalOptions & { children: ReactNode }) => (
  <Dialog.Content className={className ?? (maxWidth ? maxWidthClassMap[maxWidth] : "max-w-none")}>
    {header !== undefined ? (
      header
    ) : (
      <DialogHeader title={title} controls={{ allowResize }} />
    )}
    <Dialog.Body style={dialogContentStyle}>
      {children}
    </Dialog.Body>
  </Dialog.Content>
);

/**
 * Shorthand hook for opening simple dialogs via the workspace layer.
 * Handles the `pushDialog` / `popDialog` wiring and wraps content in a standard
 * `Dialog.Content` + `Dialog.Header` shell so callers only need to provide a title and children.
 *
 * Use this for straightforward cases. For dialogs that need custom header chrome,
 * non-standard intent, or a different layer, use `useDialog().pushDialog(...)` directly.
 */
export const useDialogModal = () => {
  const { pushDialog, popDialog } = useDialog();

  const openDialogModal = useCallback(
    (options: DialogModalOptions, children: ReactNode | ((props: { close: () => void }) => ReactNode)) =>
      pushDialog({
        layer: WORKSPACE_LAYER,
        options: { intent: "form" },
        component: ({ close }) => (
          <DialogModalContent {...options}>
            {typeof children === "function" ? children({ close }) : children}
          </DialogModalContent>
        ),
      }),
    [pushDialog],
  );

  const closeDialogModal = useCallback(() => popDialog({ layer: WORKSPACE_LAYER }), [popDialog]);

  // stable return — prevents consumers from re-rendering on every call
  return useMemo(() => ({ openDialogModal, closeDialogModal }), [openDialogModal, closeDialogModal]);
};
